<template>
  <div class="float-select-container" @click.stop>
    <!-- 触发按钮 -->
    <div 
      class="trigger-btn" 
      :class="{ active: visible }"
      @click="toggleVisible"
    >
      <!-- 优化显示文本：如果是对象数组，显示名称；否则显示 placeholder -->
      <span class="btn-text">{{ placeholder }}</span>
      <img src="@/assets/images/ico_down2@2x.png" class="arrow-icon" :class="{ rotate: visible }">
    </div>

    <!-- 悬浮面板 -->
    <transition name="fade-slide">
      <div v-if="visible" class="float-panel">
        <div class="panel-content">
          <!-- 遍历选项 -->
          <div 
            v-for="(item, index) in options" 
            :key="index"
            class="option-item"
            @click="handleSelect(item)"
          >
            <span class="item-text">{{ item[nameKey] }}</span>
            <!-- 选中后的红色对勾 -->
            <img v-if="isSelected(item)" src="@/assets/images/icon-floor-list-item.png" class="check-icon">
          </div>
          
          <!-- 空状态 -->
          <div v-if="!options.length" class="empty-state">暂无数据</div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
// 【新增】创建一个简单的单例管理器，用于存储当前打开的组件实例
const FloatSelectManager = {
  activeInstance: null,
  
  // 注册当前激活的实例
  register(instance) {
    // 如果之前有激活的实例，且不是当前这个，先关闭它
    if (this.activeInstance && this.activeInstance !== instance) {
      this.activeInstance.close();
    }
    this.activeInstance = instance;
  },
  
  // 注销实例（当组件销毁或手动关闭时）
  unregister(instance) {
    if (this.activeInstance === instance) {
      this.activeInstance = null;
    }
  },
  
  // 强制关闭所有（可选，用于点击全局空白处）
  closeAll() {
    if (this.activeInstance) {
      this.activeInstance.close();
      this.activeInstance = null;
    }
  }
};
export default {
  name: 'FloatSelect',
  props: {
    // 绑定值：现在支持 对象数组(多选) 或 单个对象(单选)
    value: {
      type: [Array, String, Number, Object],
      default: () => []
    },
    // 选项数据源
    options: {
      type: Array,
      default: () => []
    },
    // 是否多选
    multiple: {
      type: Boolean,
      default: true
    },
    // 显示字段名
    nameKey: {
      type: String,
      default: 'name'
    },
    // 值字段名 (用于内部比对唯一性)
    valueKey: {
      type: String,
      default: 'value'
    },
    // 占位符
    placeholder: {
      type: String,
      default: '请选择'
    }
  },
  data() {
    return {
      visible: false
    };
  },
  computed: {
    // 计算按钮显示的文本
    displayText() {
      if (!this.value) return this.placeholder;

      // 多选情况
      if (this.multiple) {
        if (Array.isArray(this.value) && this.value.length > 0) {
          // 如果数组里存的是对象，取第一个对象的 nameKey 显示，或者显示数量
          const firstItem = this.value[0];
          if (typeof firstItem === 'object' && firstItem !== null && firstItem[this.nameKey]) {
            return this.value.length === 1 
              ? firstItem[this.nameKey] 
              : `已选 ${this.value.length} 项`;
          }
          // 兼容旧数据（如果是 ID 数组）
          return `已选 ${this.value.length} 项`;
        }
        return this.placeholder;
      } 
      // 单选情况
      else {
        if (typeof this.value === 'object' && this.value !== null && this.value[this.nameKey]) {
          return this.value[this.nameKey];
        }
        // 兼容旧数据（如果是 ID）
        if (this.value) {
           const item = this.options.find(opt => opt[this.valueKey] === this.value);
           return item ? item[this.nameKey] : this.placeholder;
        }
        return this.placeholder;
      }
    }
  },
  methods: {
    toggleVisible() {
      if (this.visible) {
        this.close();
      } else {
        this.open();
      }
    },
    open() {
      this.visible = true;
      // 注册自己，管理器会自动关闭上一个打开的组件
      FloatSelectManager.register(this);
    },
    
    close() {
      this.visible = false;
      // 注销自己
      FloatSelectManager.unregister(this);
    },
    // 判断是否选中
    isSelected(item) {
      const itemVal = item[this.valueKey];
      
      if (this.multiple) {
        if (!Array.isArray(this.value)) return false;
        // 遍历当前绑定的值，检查是否有相同的 valueKey
        return this.value.some(v => {
          // 如果 v 是对象，对比 valueKey；如果 v 是基本类型，直接对比
          const vVal = (typeof v === 'object' && v !== null) ? v[this.valueKey] : v;
          return vVal === itemVal;
        });
      } else {
        // 单选
        if (typeof this.value === 'object' && this.value !== null) {
          return this.value[this.valueKey] === itemVal;
        }
        return this.value === itemVal;
      }
    },

    // 处理点击选择
    handleSelect(item) {
      if (this.multiple) {
        // 确保 newVal 是数组
        let newVal = Array.isArray(this.value) ? [...this.value] : [];
        
        // 检查该项是否已存在
        const existsIndex = newVal.findIndex(v => {
          const vVal = (typeof v === 'object' && v !== null) ? v[this.valueKey] : v;
          return vVal === item[this.valueKey];
        });

        if (existsIndex > -1) {
          // 取消选中：移除该对象
          newVal.splice(existsIndex, 1);
        } else {
          // 选中：存入完整对象
          // 为了防止引用问题，可以浅拷贝一下对象，或者直接存 item
          newVal.push({ ...item }); 
        }
        
        this.$emit('input', newVal);
        this.$emit('change', newVal);
      } else {
        // 单选逻辑
        const isSame = this.isSelected(item);
        
        // 如果点击已选中的，则清空；否则存入完整对象
        const newVal = isSame ? null : { ...item };
        
        this.$emit('input', newVal);
        this.$emit('change', newVal);
        
        // 单选通常选择后自动关闭
        if (!isSame) {
          this.close();
        }
      }
    },

    // 点击外部关闭
    handleClickOutside(e) {
      // 只有当当前组件是激活状态，且点击的不是自己内部时，才关闭
      if (FloatSelectManager.activeInstance === this && !this.$el.contains(e.target)) {
        this.close();
      }
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
    // 组件销毁时确保清理管理器状态
    FloatSelectManager.unregister(this);
  }
};
</script>

<style lang="scss" scoped>
/* 样式保持不变 */
.float-select-container {
  position: relative;
  display: inline-block;

  .trigger-btn {
    display: inline-flex;
    align-items: center;
    padding: 0 4px;
    background: transparent;
    border: none;
    cursor: pointer;
    font-size: 14px;
    color: #333;
    transition: color 0.3s;
    user-select: none;

    &:hover, &.active {
      color: #ff5967;
    }

    .btn-text {
      margin-right: 8px;
      font-weight: 400;
      font-size: 14px;
      color: #333333;
      text-align: left;
      font-style: normal;
      text-transform: none;
      max-width: 200px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .arrow-icon {
      width: 12px;
      height: 12px;
      transition: transform 0.3s;
      &.rotate {
        transform: rotate(180deg);
      }
    }
  }

  .float-panel {
    position: absolute;
    top: 100%;
    left: 0;
    z-index: 1000;
    margin-top: 8px;
    min-width: 120px;
    background: #ffffff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    border-radius: 4px;
    padding: 8px 0;
    border: 1px solid #eee;

    .panel-content {
      max-height: 300px;
      overflow-y: auto;
      display: flex;
      flex-direction: column;
    }

    .option-item {
      position: relative;
      padding: 8px 16px;
      padding-right: 30px;
      font-size: 14px;
      color: #333;
      cursor: pointer;
      transition: background-color 0.2s;
      line-height: 1.5;
      text-align: left;

      &:hover {
        background-color: #f5f7fa;
      }

      .item-text {
        display: inline-block;
      }

      .check-icon {
        position: absolute;
        right: 12px;
        top: 50%;
        transform: translateY(-50%);
        width: 14px;
        height: 14px;
      }
    }

    .empty-state {
      text-align: center;
      color: #999;
      padding: 10px 0;
      font-size: 14px;
    }
  }
}

.fade-slide-enter-active, .fade-slide-leave-active {
  transition: opacity 0.2s, transform 0.2s;
}
.fade-slide-enter, .fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}
</style>